import { FC, useState, forwardRef } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';

import {
    Box,
    Chip,
    Drawer as MuiDrawer,
    List,
    ListItemButton as MuiListItemButton,
    ListItemText,
    ListItemIcon,
    IconButton,
    Divider,
    ListSubheader,
    useMediaQuery,
    useTheme,
    ListItemButtonBaseProps,
    Tooltip,
} from '@mui/material';
import { styled, CSSObject, Theme } from '@mui/material/styles';

import LogoutIcon from '@mui/icons-material/Logout';

import { logout, accessToken, refreshToken } from '@lendica/auth';

import { useAsyncError, DaylitLogo } from '@lendica/components';
import { DaylitTheme } from '@lendica/theme';
import { useActiveCompanyKey } from '@lendica/hooks';
import { CompanySwitcher } from './components/CompanySwitcher';
import { alpha } from '@mui/material/styles';
import { navigationConfig, NavigationItem } from './config/navigation';
import { clearActiveCompanySelection } from './api/axiosInstance';

const ListItemButton = styled(MuiListItemButton)(({ theme, disabled, selected }) => ({
    'color': theme.palette.secondary.main,
    '& .MuiListItemIcon-root': {
        color: alpha(theme.palette.secondary.main, 0.6),
    },
    ...(disabled && {
        '&.Mui-disabled': { opacity: 'initial' },
        '& .MuiListItemIcon-root': {
            opacity: theme.palette.action.disabledOpacity,
        },
        '& .MuiListItemText-root': {
            opacity: theme.palette.action.disabledOpacity,
        },
    }),
    ...(selected && {
        '&.Mui-selected': {
            color: theme.palette.primary.main,
            // backgroundColor: theme.palette.action.selected,
        },
        '& .MuiListItemText-root .MuiTypography-root': {
            fontWeight: 500,
        },
        '& .MuiListItemIcon-root': { color: theme.palette.primary.main },
    }),
}));

const NavigationListItemButton = forwardRef<
    HTMLDivElement,
    { path: string } & ListItemButtonBaseProps
>(({ path, ...props }, ref) => {
    const navigate = useNavigate();
    const companyKey = useActiveCompanyKey();
    const scopedPath = companyKey ? `/c/${companyKey}${path}` : path;
    const match = useMatch(`/c/:companyKey${path}/*`);

    return (
        <ListItemButton
            {...props}
            ref={ref}
            dense
            selected={!!match}
            onClick={() => navigate(scopedPath)}
        />
    );
});

const drawerWidth = 240; // 304;
const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    overflowX: 'hidden',
    px: 1,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
});
const closedMixin = (theme: Theme): CSSObject => ({
    overflowX: 'hidden',
    width: 64, // 96
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.shortest,
    }),
});

interface SidebarProps {
    open: boolean;
}

export const Sidebar: FC<SidebarProps> = ({ open }) => {
    const theme = useTheme();

    const navigate = useNavigate();

    const throwError = useAsyncError();

    const handleLogout = async () => {
        try {
            await logout('auth');
            refreshToken.remove();
            accessToken.remove();
            clearActiveCompanySelection();
        } catch (error: any) {
            console.log(error);
            if (error!.response && error!.response!.status === 401) {
                refreshToken.remove();
                accessToken.remove();
                navigate('/auth');
                return;
            }
            throwError(new Error(`Portal error on logout: ${error}`));
        }
    };

    const SidebarItem: FC<NavigationItem> = ({
        label,
        icon,
        path,
        onClick,
        testId,
        id,
        new: newFeatureFlag,
    }) => {
        const content = path ? (
            <NavigationListItemButton
                path={path}
                data-testid={testId}
                {...(id ? { 'aria-labelledby': id } : {})}
            >
                <ListItemIcon>{icon}</ListItemIcon>
                {open && <ListItemText primary={label} />}
                {open && newFeatureFlag && <NewFeatureBadge />}
            </NavigationListItemButton>
        ) : (
            <ListItemButton dense onClick={onClick} data-testid={testId} id={id}>
                <ListItemIcon>{icon}</ListItemIcon>
                {open && <ListItemText primary={label} />}
                {open && newFeatureFlag && <NewFeatureBadge />}
            </ListItemButton>
        );

        return !open ? (
            <Tooltip
                title={label}
                placement="right"
                enterDelay={0}
                slotProps={{
                    popper: { modifiers: [{ name: 'offset', options: { offset: [0, -6] } }] },
                }}
            >
                {content}
            </Tooltip>
        ) : (
            content
        );
    };

    return (
        <MuiDrawer
            sx={[
                {
                    'width': drawerWidth,
                    'flexShrink': 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        background: 'transparent',
                        border: 'none',
                    },
                    '& .MuiListItemIcon-root': {
                        my: 0.5,
                        pl: 2,
                    },
                    '& .MuiListItemButton-root': {
                        px: 0,
                    },
                },
                theme =>
                    open
                        ? {
                              ...openedMixin(theme),
                              '& .MuiDrawer-paper': openedMixin(theme),
                              '& .MuiListItemButton-root': {
                                  borderRadius: '6px',
                              },
                          }
                        : {
                              ...closedMixin(theme),
                              '& .MuiDrawer-paper': closedMixin(theme),
                              '& .MuiListItemButton-root': {
                                  flexWrap: 'wrap',
                                  justifyContent: 'center',
                              },
                              '& .MuiListItemText-root': {
                                  width: '100%',
                              },
                              '& .MuiListItemText-root .MuiTypography-root': {
                                  textAlign: 'center',
                                  fontSize: 'caption.fontSize',
                              },
                          },
            ]}
            variant="permanent"
            open={open}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: open ? 'space-between' : 'center',
                    alignItems: 'center',
                    pt: open ? 2 : 0.35,
                    pb: 1,
                }}
            >
                {open && (
                    <IconButton
                        size="large"
                        onClick={() => {
                            navigate('/');
                        }}
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                    >
                        <DaylitLogo height={18} color={DaylitTheme.Palette.Burgundy} />
                    </IconButton>
                )}
            </Box>
            <CompanySwitcher expanded={open} />
            <List>
                {navigationConfig.main.map(item => (
                    <SidebarItem key={item.path ?? item.label} {...item} />
                ))}
                {!open && <Divider />}
                {open && (
                    <ListSubheader component="div" sx={{ backgroundColor: 'inherit' }}>
                        Pay
                    </ListSubheader>
                )}
                {navigationConfig.pay.map(item => (
                    <SidebarItem key={item.path ?? item.label} {...item} />
                ))}
                {open && (
                    <ListSubheader component="div" sx={{ backgroundColor: 'inherit' }}>
                        Collect
                    </ListSubheader>
                )}
                {navigationConfig.collect.map(item => (
                    <SidebarItem key={item.path ?? item.label} {...item} />
                ))}
                <Divider />
                {navigationConfig.other.map(item => (
                    <SidebarItem key={item.label} {...item} />
                ))}
                {open && (
                    <ListSubheader component="div" sx={{ backgroundColor: 'inherit' }}>
                        Settings
                    </ListSubheader>
                )}
                {navigationConfig.settings.map(item => (
                    <SidebarItem key={item.path ?? item.label} {...item} />
                ))}
                <SidebarItem
                    label="Logout"
                    icon={<LogoutIcon />}
                    onClick={handleLogout}
                    testId="logout-button"
                    id={'logout-button'}
                />
            </List>
        </MuiDrawer>
    );
};

const NewFeatureBadge = () => {
    return (
        <Chip
            label="NEW"
            size="small"
            variant="outlined"
            color="primary"
            sx={{
                'height': '20px',
                'fontSize': '10px',
                'fontWeight': 'bold',
                'borderRadius': '10px',
                'ml': 0.75,
                'mr': 1,
                '& .MuiChip-label': {
                    px: 0.75,
                },
            }}
        />
    );
};
